// src/components/PortalAPI.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaCode, FaMoneyBillWave, FaHeadset, FaUsers, 
  FaCopy, FaCheckCircle, FaKey, FaExchangeAlt, 
  FaPlug, FaInfoCircle, FaPlus, FaSearch, FaBan
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../services/api';
import './PortalAPI.css';
import ModalPortalAPI from './ModalPortalAPI'; // <--- O novo componente isolado!

export default function PortalAPI({ user }) {
  const [activeTab, setActiveTab] = useState('os');
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [seguradoras, setSeguradoras] = useState([]);

  const getAuthConfig = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const loadSeguradoras = useCallback(async () => {
    try {
      const res = await api.get('/api/integracoes/seguradoras', getAuthConfig());
      setSeguradoras(res.data || []);
    } catch (e) {
      console.error("Erro ao carregar parceiros", e);
    }
  }, [getAuthConfig]);

  useEffect(() => {
    loadSeguradoras();
  }, [loadSeguradoras]);

  const copiar = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const tabs = [
    { id: 'os', label: 'Integração de OS', icon: <FaCode style={{ marginRight: '10px' }} /> },
    { id: 'financeiro', label: 'Financeiro', icon: <FaMoneyBillWave style={{ marginRight: '10px' }} /> },
    { id: 'webhook', label: 'Webhooks (Retorno)', icon: <FaExchangeAlt style={{ marginRight: '10px' }} /> },
    { id: 'clientes', label: 'Clientes', icon: <FaUsers style={{ marginRight: '10px' }} /> },
  ];

  return (
    <div className="api-portal-container">
      {/* Sidebar */}
      <aside className="api-sidebar">
        <div className="api-sidebar-header">
          <div className="api-logo">
            <FaPlug color="#0ea5e9" style={{ marginRight: '8px' }} />
            <span>Dev Center</span>
          </div>
          <p>Documentação Técnica</p>
        </div>
        <nav className="api-nav">
          <div className="api-nav-label">Módulos</div>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`api-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="api-nav-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
          
          <div className="api-nav-label" style={{marginTop: '20px'}}>Segurança</div>
          <button className="api-nav-btn key-manager" onClick={() => setIsKeyModalOpen(true)}>
            <FaKey className="api-nav-icon" style={{ marginRight: '10px' }} />
            Gerenciar Credenciais
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="api-content">
        {activeTab === 'os' && (
          <div className="api-doc-wrapper animate-fade-in">
            <div className="api-text-col">
              <header className="api-doc-header">
                <span className="api-badge">v1.0</span>
                <h1>REST API: Ordens de Serviço</h1>
                <p>
                  Utilize nossos endpoints para criar, consultar e gerenciar o ciclo de vida completo de uma OS.
                  Nossa API foi projetada para ser <strong>Agnóstica</strong>, com adaptadores automáticos para os principais formatos do mercado.
                </p>
              </header>

              <div className="api-auth-notice" style={{ marginTop: '24px' }}>
                 <FaInfoCircle style={{ marginRight: '10px', fontSize: '18px' }} />
                 <span>Todas as requisições exigem o cabeçalho <code>Authorization: Bearer SUA_CHAVE</code>.</span>
              </div>

              {/* ── 1. POST: CRIAR OS ── */}
              <div className="api-section">
                <h3 style={{ display: 'flex', alignItems: 'center' }}>
                  <FaPlus style={{ marginRight: '8px', color: '#10b981', fontSize: '14px' }} /> 
                  1. Criar Nova OS (Inbound)
                </h3>
                <p>Cria um novo chamado. Se o <code>protocolo</code> já existir, retornaremos a OS correspondente para evitar duplicidade.</p>
                <div className="api-params-table">
                  <div className="param-row">
                    <div className="param-name">protocolo <span>string</span> <small>req</small></div>
                    <div className="param-desc">Identificador único da seguradora/parceiro.</div>
                  </div>
                  <div className="param-row">
                    <div className="param-name">solicitante.nome <span>string</span> <small>req</small></div>
                    <div className="param-desc">Nome completo do motorista ou dono do veículo.</div>
                  </div>
                  <div className="param-row">
                    <div className="param-name">atendimento.local <span>string</span> <small>req</small></div>
                    <div className="param-desc">Endereço de coleta do veículo.</div>
                  </div>
                </div>
              </div>

              {/* ── 2. GET: CONSULTAR OS ── */}
              <div className="api-section">
                <h3 style={{ display: 'flex', alignItems: 'center' }}>
                  <FaSearch style={{ marginRight: '8px', color: '#3b82f6', fontSize: '14px' }} /> 
                  2. Consultar Status da OS
                </h3>
                <p>Retorna o status em tempo real de uma OS utilizando o protocolo enviado na criação.</p>
                <div className="api-params-table">
                  <div className="param-row">
                    <div className="param-name">protocolo <span>string (Path)</span> <small>req</small></div>
                    <div className="param-desc">O protocolo informado na criação.</div>
                  </div>
                </div>
              </div>

              {/* ── 3. PUT/DELETE: CANCELAR OS ── */}
              <div className="api-section">
                <h3 style={{ display: 'flex', alignItems: 'center' }}>
                  <FaBan style={{ marginRight: '8px', color: '#ef4444', fontSize: '14px' }} /> 
                  3. Cancelar OS
                </h3>
                <p>Permite que a seguradora cancele uma OS que ainda não foi iniciada ou faturada.</p>
                <div className="api-params-table">
                  <div className="param-row">
                    <div className="param-name">motivo <span>string</span></div>
                    <div className="param-desc">Opcional. Justificativa do cancelamento.</div>
                  </div>
                </div>
              </div>

            </div>

            {/* COLUNA DE CÓDIGO */}
            <div className="api-code-col">
              
              {/* Card de Código: POST */}
              <div className="api-endpoint-card" style={{ marginBottom: '32px' }}>
                <div className="endpoint-header">
                  <span className="api-method post">POST</span>
                  <code>/api/public/seguradoras/:slug/webhook</code>
                </div>
                <div className="code-block">
                  <div className="code-header">
                    <span>Requisição (JSON)</span>
                    <button onClick={() => copiar('JSON Create...', 'Copiado!')}><FaCopy style={{ marginRight: '4px' }}/> Copiar</button>
                  </div>
                  <pre>
{`{
  "protocolo": "2026-XPT-99",
  "solicitante": {
    "nome": "Bruno Oliveira",
    "telefone": "11988887777"
  },
  "atendimento": {
    "local": "Av. Paulista, 1000 - SP",
    "descricao": "Pane Seca"
  },
  "veiculo": {
    "placa": "BRA2E22"
  }
}`}
                  </pre>
                </div>
                <div className="code-block success">
                  <div className="code-header"><span><FaCheckCircle style={{ marginRight: '6px', color: '#10b981' }}/> Resposta (201 Created)</span></div>
                  <pre>{`{ "ok": true, "osId": "0526-0041", "message": "OS Criada" }`}</pre>
                </div>
              </div>

              {/* Card de Código: GET */}
              <div className="api-endpoint-card" style={{ marginBottom: '32px' }}>
                <div className="endpoint-header">
                  <span className="api-method get" style={{ background: '#3b82f6', color: '#fff' }}>GET</span>
                  <code>/api/public/seguradoras/:slug/os/:protocolo</code>
                </div>
                <div className="code-block success">
                  <div className="code-header"><span><FaCheckCircle style={{ marginRight: '6px', color: '#10b981' }}/> Resposta (200 OK)</span></div>
                  <pre>
{`{
  "osId": "0526-0041",
  "protocolo": "2026-XPT-99",
  "status": "Em Andamento",
  "motorista": {
    "nome": "João da Silva",
    "contato": "11977776666"
  },
  "previsao_chegada": "25 min"
}`}
                  </pre>
                </div>
              </div>

              {/* Card de Código: PUT (Cancelar) */}
              <div className="api-endpoint-card">
                <div className="endpoint-header">
                  <span className="api-method put" style={{ background: '#f59e0b', color: '#fff' }}>PUT</span>
                  <code>/api/public/seguradoras/:slug/os/:protocolo/cancelar</code>
                </div>
                <div className="code-block">
                  <div className="code-header">
                    <span>Requisição (JSON)</span>
                  </div>
                  <pre>
{`{
  "motivo": "Cliente conseguiu ligar o veículo e seguiu viagem."
}`}
                  </pre>
                </div>
                <div className="code-block success">
                  <div className="code-header"><span><FaCheckCircle style={{ marginRight: '6px', color: '#10b981' }}/> Resposta (200 OK)</span></div>
                  <pre>{`{ "ok": true, "message": "OS Cancelada com sucesso" }`}</pre>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* ABA WEBHOOK */}
        {activeTab === 'webhook' && (
          <div className="api-doc-wrapper animate-fade-in">
             <div className="api-text-col">
                <h1>Callback de Atualização (Outbound)</h1>
                <p>Nossa plataforma notifica seu sistema automaticamente sempre que a OS mudar de status (ex: <i>Em Andamento</i>, <i>Concluído</i>).</p>
                
                <div className="api-auth-notice">
                   <FaInfoCircle style={{ marginRight: '10px', fontSize: '18px' }} />
                   <span>O endpoint de destino deve ser configurado no campo <strong>Endpoint Outbound</strong> do seu painel.</span>
                </div>

                <h3>Payload de Retorno</h3>
                <p>Enviaremos um JSON completo no método configurado (POST/PUT) contendo:</p>
                <ul>
                  <li>Status atualizado da OS.</li>
                  <li><strong>Evidências:</strong> Links seguros do Drive com as fotos colhidas pelo motorista.</li>
                  <li>Dados operacionais (Motorista, Placa do Guincho).</li>
                </ul>
             </div>
             <div className="api-code-col">
                <div className="code-block">
                  <div className="code-header"><span>Exemplo de Payload Enviado ao Seu Servidor</span></div>
                  <pre>
{`{
  "os_id": "0526-0041",
  "protocolo": "2026-XPT-99",
  "status": "concluido",
  "data_conclusao": "2026-05-11T21:00:00",
  "motorista": {
    "nome": "João da Silva",
    "telefone": "11977776666"
  },
  "anexos": [
    {
      "nome": "frente_veiculo.jpg",
      "url": "https://drive.google.com/uc?id=XYZ..."
    }
  ]
}`}
                  </pre>
                </div>
             </div>
          </div>
        )}

        {/* Placeholders para outras abas */}
        {['financeiro', 'clientes'].includes(activeTab) && (
          <div className="api-doc-section" style={{ padding: '60px 48px' }}>
            <header className="api-doc-header">
              <h1>Em construção</h1>
              <p>A documentação técnica para o módulo de <strong>{tabs.find(t => t.id === activeTab)?.label}</strong> será disponibilizada em breve.</p>
            </header>
          </div>
        )}
      </main>

      {/* Componente Modal Isolado */}
      <ModalPortalAPI 
        isOpen={isKeyModalOpen} 
        onClose={() => setIsKeyModalOpen(false)} 
        seguradoras={seguradoras}
        getAuthConfig={getAuthConfig}
        onRefresh={loadSeguradoras}
      />

    </div>
  );
}